import { zodResolver } from "@hookform/resolvers/zod";
import CloudDownload from "@mui/icons-material/CloudDownload";
import Delete from "@mui/icons-material/Delete";
import Folder from "@mui/icons-material/Folder";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";

import { useCallback, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import FormTextField from "../components/Common/FormTextField";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import { useConfigs, useCreateAsset } from "../components/Orion/OrionQueries";
import NewAssetPageHeader from "../components/PageHeader/NewAssetPage";
import { NewAssetForm, newAssetSchema } from "../schemas/NewAsset";

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "image/*": [".png", ".jpg", ".jpeg"],
};

function NewAssetPage() {
  const [error, setError] = useState<string | null>(null);
  const [singleAsset, setSingleAsset] = useState<boolean>(true);

  const { mutate: createAsset } = useCreateAsset();
  const { data: configs } = useConfigs();

  const { control, ...methods } = useForm<NewAssetForm>({
    defaultValues: {
      config_id: "",
      files: [],
      name: "",
      workspace_id: "",
    },

    resolver: zodResolver(newAssetSchema),
  });

  const { files } = methods.watch();

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        setError(
          "Some files were rejected. Please only upload PDFs or images within the size limit."
        );
        return;
      }

      methods.setValue("files", [...files, ...acceptedFiles]);
    },
    [methods, files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
  });

  const onSubmit: SubmitHandler<NewAssetForm> = (data) => {
    console.log("Submitted data", data);

    if (singleAsset) {
      createAsset(data);
    } else {
      data.files.forEach((oneFile) => {
        createAsset({
          ...data,
          files: [oneFile],
          name: oneFile.name,
        });
      });
    }

    methods.reset();
  };

  const onError: SubmitErrorHandler<NewAssetForm> = (errors) => {
    console.log("Errored data", methods.watch());
    console.log("Errors", errors);
  };

  return (
    <DashboardLayout>
      <Container maxWidth={false} className="overflow-hidden">
        <Stack spacing={2} className="py-2 h-full overflow-hidden">
          <NewAssetPageHeader
            singleAsset={singleAsset}
            setSingleAsset={setSingleAsset}
          />
          <form
            onSubmit={methods.handleSubmit(onSubmit, onError)}
            className="flex flex-col gap-4 overflow-hidden"
          >
            <div
              {...getRootProps()}
              className="flex flex-col items-center cursor-pointer text-[#4C4FA2] bg-[#3b2fc32a] border-[#3b2fc32a] border-dashed border-2 rounded-lg p-4"
            >
              <input {...getInputProps()} />
              <CloudDownload sx={{ fontSize: 96 }} />
              {isDragActive ? (
                <p className="font-bold">Drop the files here ...</p>
              ) : (
                <p className="font-bold">
                  Glissez & déposez, ou parcourez vos fichiers
                </p>
              )}
              {error && (
                <Alert color="error" className="mt-3">
                  {error}
                </Alert>
              )}
            </div>
            <List className="overflow-auto">
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() =>
                        methods.setValue(
                          "files",
                          files.filter((file, idx) => idx !== index)
                        )
                      }
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      <Folder />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText>{file.name}</ListItemText>
                </ListItem>
              ))}
            </List>

            {singleAsset && (
              <FormTextField
                control={control}
                label="Nom du dossier"
                name="name"
                error={!!methods.formState.errors.name}
                helperText={methods.formState.errors.name?.message}
              />
            )}
            <Controller
              render={({ field, fieldState }) => {
                const selectedConfig =
                  configs?.find((config) => config._id === field.value) || null;
                return (
                  <Autocomplete
                    options={configs || []}
                    getOptionLabel={(config) => config.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Configuration"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        fullWidth
                      />
                    )}
                    value={selectedConfig}
                    onChange={(e, value) => {
                      field.onChange(value?._id ?? "");
                      methods.setValue(
                        "workspace_id",
                        value?.workspace_id ?? ""
                      );
                    }}
                    isOptionEqualToValue={(option, value) =>
                      option._id === value._id
                    }
                  />
                );
              }}
              name={`config_id`}
              control={control}
            />

            <Button
              className="w-100"
              type="submit"
              disabled={files.length === 0 || methods.formState.isSubmitting}
              variant="contained"
            >
              {methods.formState.isSubmitting ? (
                <CircularProgress className="text-[var(--mui-palette-primary-main)]" />
              ) : (
                "Créer le dossier"
              )}
            </Button>
          </form>
        </Stack>
      </Container>
    </DashboardLayout>
  );
}

export default NewAssetPage;
